// Permission types for the access control system
export type Permission =
  // Business permissions
  | "business:create"
  | "business:read"
  | "business:update"
  | "business:delete"
  | "business:list"

  // Branch permissions
  | "branch:create"
  | "branch:read"
  | "branch:update"
  | "branch:delete"
  | "branch:list"
  | "branch:assign_drivers"
  | "branch:manage_coverage"
  | "branch:deactivate"

  // Driver permissions - Enhanced for Ticket 2
  | "driver:create"
  | "driver:update"
  | "driver:deactivate"

  // Map permissions - New for Ticket 3
  | "map:view_full"
  | "map:view_partial";

// Role definitions with their permissions
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: [
    // Full access to everything
    "business:create",
    "business:read",
    "business:update",
    "business:delete",
    "business:list",
    "branch:create",
    "branch:read",
    "branch:update",
    "branch:delete",
    "branch:list",
    "branch:assign_drivers",
    "branch:manage_coverage",
    "branch:deactivate",
    "driver:create",
    "driver:update",
    "driver:deactivate",
    "map:view_full",
  ],
  manager: [
    // Business administrator - full access to assigned businesses
    "business:read",
    "business:update",
    "business:list",
    "branch:create",
    "branch:read",
    "branch:update",
    "branch:delete",
    "branch:list",
    "branch:assign_drivers",
    "branch:manage_coverage",
    "branch:deactivate",
    "map:view_partial",
  ],

  driver: [
    // Driver - minimal permissions, self-service and assigned orders
  ],
};

// Resource access control
export interface ResourceAccess {
  brandIds: number[];
  branchIds: number[];
  canAccessAll: boolean;
}

// Permission context interface
export interface PermissionContextType {
  permissions: Permission[];
  resourceAccess: ResourceAccess;
  hasPermission: (permission: Permission) => boolean;
  hasBrandAccess: (brandId: number) => boolean;
  hasBranchAccess: (branchId: number) => boolean;
  hasBranchAccessByBrand: (brandId: number) => boolean;
  canAccessResource: (
    resourceType: "business" | "branch",
    resourceId: number,
  ) => boolean;
  getAccessibleBrands: () => number[];
  getAccessibleBranches: () => number[];
  getPermissionMatrix: () => Record<string, Permission[]>;
}

// Action types for audit logging
export type AuditAction =
  | "business:created"
  | "business:updated"
  | "business:deleted"
  | "branch:created"
  | "branch:updated"
  | "branch:deleted"
  | "branch:deactivated"
  | "driver:created"
  | "driver:updated"
  | "driver:deleted"
  | "driver:assigned"
  | "driver:status_changed"
  | "driver:category_changed"
  | "driver:personal_info_updated"
  | "driver:work_info_updated"
  | "driver:deactivated"
  | "map:data_exported"
  | "map:sensitive_info_accessed"
  | "map:analytics_accessed"
  | "order:status_updated"
  | "order:cancelled"
  | "order:sensitive_info_accessed"
  | "coverage:updated"
  | "user:created"
  | "user:updated"
  | "user:deactivated"
  | "permissions:granted"
  | "permissions:revoked";

export interface AuditLog {
  id: string;
  userId: string;
  userEmail: string;
  action: AuditAction;
  resourceType: string;
  resourceId: number;
  details: Record<string, any>;
  timestamp: Date;
  ipAddress?: string;
}

// Permission filters for query operations
export interface PermissionFilters {
  canAccessAll: boolean;
  accessibleBrandIds: number[];
  accessibleBranchIds: number[];
}
